import NiceModal, { useModal } from '@ebay/nice-modal-react';

import React, { useCallback, useEffect, useState } from 'react';

import Status from 'components/field/Status';
import * as F from 'components/form/FormLayout.styled';
import Modal from 'components/global/Modal';
import NoRecordsFound from 'components/global/NoRecordsFound';
import Typography from 'components/global/Typography';
import { ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import Table from 'components/table/Table';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

import { PRICE_CONFIGURATIONS } from 'services/consts';

import AppLogger from 'utils/AppLogger';

/**
 * @author Burak Ak
 */

const PCPriceMatcher = NiceModal.create(({ otoOrderIDs }) => {
	const axiosPrivate = useAxiosPrivate();
	const modal = useModal();
	const { showError } = useAlert();

	const [isLoading, setIsLoading] = useState(false);
	const [data, setData] = useState([]);

	useEffect(() => {
		if (otoOrderIDs) {
			fillForm();
		}
	}, []);

	const handleCloseModal = () => {
		modal.remove();
	};

	const fillForm = useCallback(async () => {
		setIsLoading(true);
		try {
			const response = await axiosPrivate.post(PRICE_CONFIGURATIONS.GET_AGGREGATOR_SHIPMENT_LIST, {
				ids: otoOrderIDs
			});

			if (response.status === 200) {
				setData(response.data.data);
			}
		} catch (error) {
			if (error.response?.data.code === 'error.PC001') {
				showError({
					message: error?.response?.data?.code || error?.response?.data?.message
				});
				handleCloseModal();
			} else {
				AppLogger.error(error);
				showError({
					message: error?.response?.data?.code || error?.response?.data?.message
				});
			}
		} finally {
			setIsLoading(false);
		}
	}, []);

	return (
		<Modal maxWidth="no-max-width" fullwidth className="modal-pcPriceMatcher">
			<ModalTitle>
				<Typography variant="p1">pcPriceMatcherForm</Typography>
			</ModalTitle>
			<F.TabsFormContainer>
				<ModalContent style={{ width: '100%' }}>
					<PCPriceMatcherTable data={data} isLoading={isLoading} />
				</ModalContent>
			</F.TabsFormContainer>
		</Modal>
	);
});

export default PCPriceMatcher;

const PCPriceMatcherTable = ({ data, isLoading }) => {
	const columns = React.useMemo(
		() => [
			{
				header: 'orderNumber',
				accessorKey: 'orderId',
				size: 70,
				showOnMobile: true
			},
			{
				header: 'shipmentNumber',
				accessorKey: 'shipmentNumber',
				size: 90
			},
			{
				header: 'clientName',
				accessorKey: 'clientName',
				size: 90
			},
			{
				header: 'packageName',
				accessorKey: 'packageName',
				size: 75
			},
			{
				header: 'deliveryCompany',
				accessorKey: 'deliverCompanyName',
				size: 100,
				renderCell: ({ row }) => (
					<Typography>
						{`${row.original.deliverCompanyName} (${row.original.DCSettingId.toString()})`}
					</Typography>
				)
			},
			{
				header: 'weight',
				accessorKey: 'weight',
				size: 40
			},
			{
				header: 'paymentMethod',
				accessorKey: 'paymentMethod',
				size: 85
			},
			{
				header: 'city',
				accessorKey: 'city',
				size: 30
			},
			{
				header: 'originCity',
				accessorKey: 'originCity',
				size: 50
			},
			{
				header: 'originTier',
				accessorKey: 'originTier',
				size: 50
			},
			{
				header: 'destinationTier',
				accessorKey: 'destinationTier',
				size: 80
			},
			{
				header: 'grandTotal',
				accessorKey: 'grandTotal',
				size: 60
			},
			{
				header: 'cod',
				accessorKey: 'totalDue',
				size: 85
			},
			{
				header: 'shippingPrice',
				accessorKey: 'OTOdeliveryFee',
				size: 70
			},
			{
				header: 'newPrice',
				accessorKey: 'newPrice',
				size: 45
			},
			{
				header: 'priceMatched',
				accessorKey: 'status',
				size: 70,
				renderCell: ({ row }) => <>{row.original.status ? <Status value={row.original.status} /> : ''}</>,
				showOnMobile: true
			}
		],
		[]
	);

	return (
		<Table
			columns={columns}
			data={data}
			loading={isLoading}
			noRecordsFound={
				<NoRecordsFound lottieName="no-items" height="10rem" title="noItems" subTitle="noItemsSubtitle" />
			}
		/>
	);
};
